import Stack from '@mui/material/Stack'
import { Grid, styled, Typography, Paper, Box, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'
import { useAppTranslation } from 'src/admin/core/hooks'
import { formatAsCurrency } from 'src/admin/core'
import { formatToShortLocalDate, formatToShortLocalDateTime } from 'src/admin/core/services/dateFunctions'
import { ConsolidatedData } from '..'
import { getAppBarState } from 'src/store'
import React, { useRef, forwardRef, useImperativeHandle } from 'react'
import jsPDF from 'jspdf'

const Label = styled(Grid)(({ theme }) => ({
  fontWeight: 'bold',
}))

export const PrintReport = forwardRef(({ data, fromDate, toDate, provider, enterpriseName, productName }: { data: ConsolidatedData[]; fromDate: string; toDate: string; provider: string; enterpriseName?: string; productName?: string }, ref) => {
  const { label } = useAppTranslation()
  const {
    user: { profile },
  } = getAppBarState().appState
  const firstRecord = data.length > 0 ? data[0] : null
  const now = new Date()

  // Group data by enterprise
  const groupedByEnterprise = data.reduce((acc, item) => {
    const enterpriseId = item.enterpriseId
    if (!acc[enterpriseId]) {
      acc[enterpriseId] = {
        enterpriseName: item.enterpriseName,
        data: []
      }
    }
    acc[enterpriseId].data.push(item)
    return acc
  }, {} as Record<string, { enterpriseName: string; data: ConsolidatedData[] }>)

  // Sort enterprises by name
  const sortedEnterprises = Object.values(groupedByEnterprise).sort((a, b) => 
    a.enterpriseName.localeCompare(b.enterpriseName)
  )

  // Ref for table
  const tableRef = useRef<HTMLDivElement>(null)

  // Expose the ref through a method that can be accessed from the parent component
  useImperativeHandle(ref, () => ({
    getTableRef: () => tableRef,
    // We no longer need footerRef since we've integrated it into the table
    downloadReport: () => {
      if (tableRef.current) {
        downloadReportAsPDF(
          tableRef,
          null, // Pass null for footerRef since we don't use it anymore
          'comisiones-consorcio',
          fromDate,
          toDate,
          provider,
          enterpriseName || '',
          sortedEnterprises,
          productName || ''
        )
      }
    }
  }));

  return (
    <Box sx={{ padding: 2 }}>
      {firstRecord && (
        <Stack direction="row" spacing={2} style={{ padding: '10px' }}>
          <Grid container spacing={2}>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid' }}>
              <span>{label('COMISIONES_POR_CONSORCIO') + (enterpriseName ? ' - ' + enterpriseName : '') + (productName ? ' - ' + productName : '')}</span>
            </Label>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid' }}>
              <Typography variant="body1" textAlign="center" style={{ fontSize: 12 }}>
                {`${label('DESDE')} ${formatToShortLocalDate(fromDate, 'YYYY-MM-DD')} ${label('HASTA')} ${formatToShortLocalDate(toDate, 'YYYY-MM-DD')} 
                ${provider?.length > 0 ? ` de ${provider}` : ''}`}
              </Typography>
            </Label>                                    
          </Grid>
        </Stack>
      )}

      <Paper elevation={0} sx={{ mt: 1, mb: 0 }}>
        {/* Table view without footer for PDF export */}
        <div ref={tableRef} data-ref="tableRef">
          {sortedEnterprises.map((enterprise, enterpriseIndex) => (
            <Box key={enterprise.enterpriseName} sx={{ mb: 3 }}>
              {/* Enterprise Header */}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: 1, 
                  mt: enterpriseIndex > 0 ? 2 : 0,
                  borderBottom: '2px solid #ccc',
                  pb: 0.5
                }}
              >
                {enterprise.enterpriseName}
              </Typography>
              
              {/* Table for this enterprise */}
              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                    <TableCell>{label('AGENCIAS')}</TableCell>
                    <TableCell>{label('ESTACION')}</TableCell>
                    <TableCell align="right">{label('VENTAS')}</TableCell>
                    <TableCell align="right">{label('CANCELACIONES')}</TableCell>
                    <TableCell align="right">{label('PAGOS')}</TableCell>
                    <TableCell align="right">{label('BALANCE')}</TableCell>
                    <TableCell align="right">{label('COMISION')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enterprise.data.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.agencyWithCode}</TableCell>
                      <TableCell>{row.workstationName}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.sales, 0)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.cancelled, 0)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.payments, 0)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.amount, 0)}</TableCell>
                      <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.commission, 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </div>
      </Paper>

      <Grid item xs={12} md={12} textAlign="right" style={{ marginTop: 10 }}>
        <Typography variant="body1" >
          {`${profile.given_name} ${formatToShortLocalDateTime(now, 'DD-MM-YYYY h:mm:ss a')}`}
        </Typography>
      </Grid>
    </Box>
  )
})

// Function to download the report as PDF
export const downloadReportAsPDF = async (
  tableRef: React.RefObject<HTMLElement>,
  footerRef?: React.RefObject<HTMLElement> | null,
  filename: string = 'comisiones-consorcio',
  fromDateStr: string = '0000-00-00',
  toDateStr: string = '0000-00-00',
  provider: string = '',
  enterpriseName: string = '',
  enterpriseGroups?: { enterpriseName: string; data: ConsolidatedData[] }[],
  productName: string = ''
) => {
  try {
    // Create new PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });
    
    // Page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Margins
    const margins = {
      top: 20,
      bottom: 20,
      left: 15,
      right: 15,
    };
    
    // Get table elements - now we need to handle multiple enterprise sections
    const tableElement = tableRef.current;
    
    if (!tableElement || !enterpriseGroups || enterpriseGroups.length === 0) {
      throw new Error('Could not find necessary elements in the table or enterprise groups');
    }
    
    // Get header from the first table
    const firstTable = tableElement.querySelector('table');
    const headerRow = firstTable?.querySelector('thead tr');
    
    if (!headerRow) {
      throw new Error('Could not find table header');
    }
    
    // Table configuration
    const fontSize = 8; // Reduced from 9 to 8 for more compact text
    const lineHeight = fontSize * 0.8; // Reduced for more compact rows
    const rowsPerPage = Math.floor((pageHeight - margins.top - margins.bottom) / lineHeight);
    
    // Set font
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    
    // Get column headers
    const headers = Array.from(headerRow.querySelectorAll('th')).map(th => th.textContent || '');
    
    // Calculate width for each column (proportional distribution)
    const columnWidths = [];
    const tableWidth = pageWidth - margins.left - margins.right;
    const numColumns = headers.length;
    
    // Give more space to the first two columns (agency name and workstation name)
    const firstColumnRatio = 0.25; // 25% of width for first column (agency)
    const secondColumnRatio = 0.20; // 20% of width for second column (workstation)
    const remainingWidthRatio = (1 - firstColumnRatio - secondColumnRatio) / (numColumns - 2);
    
    for (let i = 0; i < numColumns; i++) {
      if (i === 0) {
        columnWidths.push(tableWidth * firstColumnRatio);
      } else if (i === 1) {
        columnWidths.push(tableWidth * secondColumnRatio);
      } else {
        columnWidths.push(tableWidth * remainingWidthRatio);
      }
    }
    
    // Function to draw a row
    const drawRow = (rowData, y, isHeader = false, isFooter = false) => {
      let x = margins.left;
      
      // Set style based on row type
      if (isHeader) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 240, 240); // Light gray for header
        pdf.rect(x, y - lineHeight + 2, tableWidth, lineHeight - 1, 'F'); // Reduce rectangle height
      } else if (isFooter) {
        pdf.setFont('helvetica', 'bold');
      } else {
        pdf.setFont('helvetica', 'normal');
      }
      
      // Draw each cell in the row
      for (let i = 0; i < rowData.length; i++) {
        const cellContent = rowData[i] || '';
        const cellWidth = columnWidths[i];
        
        // Right align for numeric columns (all except the first two - agency and workstation)
        if (i > 1) {
          pdf.text(cellContent, x + cellWidth - 2, y, { align: 'right' });
        } else {
          pdf.text(cellContent, x + 2, y);
        }
        
        x += cellWidth;
      }
      
      // Restore normal font after header or footer
      if (isHeader || isFooter) {
        pdf.setFont('helvetica', 'normal');
      }
      
      return y + lineHeight;
    };
    
    // Prepare data for PDF generation - flatten all enterprise data with headers
    const pdfSections: Array<{ type: 'header' | 'data'; content: string[] | string }> = [];
    
    enterpriseGroups.forEach(enterprise => {
      // Add enterprise header
      pdfSections.push({ type: 'header', content: enterprise.enterpriseName });
      
      // Add data rows for this enterprise
      enterprise.data.forEach(row => {
        pdfSections.push({
          type: 'data',
          content: [
            row.agencyWithCode,
            row.workstationName,
            formatAsCurrency(row.sales, 0),
            formatAsCurrency(row.cancelled, 0),
            formatAsCurrency(row.payments, 0),
            formatAsCurrency(row.amount, 0),
            formatAsCurrency(row.commission, 0)
          ]
        });
      });
    });
    
    // Calculate total number of pages (rough estimate)
    const totalDataRows = enterpriseGroups.reduce((sum, enterprise) => sum + enterprise.data.length, 0);
    const totalHeaderRows = enterpriseGroups.length;
    const totalRows = totalDataRows + totalHeaderRows;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    
    // Draw each page
    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      // Add new page if not the first
      if (pageNum > 0) {
        pdf.addPage();
      }
      
      let y = margins.top;
      
      // Draw title and dates if it's the first page
      if (pageNum === 0) {
        // Title "Balance by agency"
        pdf.setFontSize(fontSize + 4); // Increase size for better visibility
        pdf.setFont('helvetica', 'bold');
        const titleParts = ['Comisiones por consorcio', enterpriseName, productName].filter(Boolean);
        pdf.text(titleParts.join(' - '), pageWidth / 2, y + lineHeight, { align: 'center' });
        y += lineHeight * 2;
        
        // Dates
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        // Get dates from component or use provided dates
        const dateText = `Desde ${fromDateStr} Hasta ${toDateStr} ${provider?.length > 0 ? ` de ${provider}` : ''}`;
        pdf.text(dateText, pageWidth / 2, y, { align: 'center' });
        y += lineHeight * 2.5; // More space after dates before table
      } else {
        y += lineHeight; // Additional space in subsequent pages
      }
      
      // Calculate which sections go on this page
      const startSection = pageNum * rowsPerPage;
      const endSection = Math.min(startSection + rowsPerPage, pdfSections.length);
      
      // Draw sections for this page
      for (let i = startSection; i < endSection; i++) {
        const section = pdfSections[i];
        
        if (section.type === 'header') {
          // Draw enterprise header
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(fontSize + 2);
          y += lineHeight; // Add some space before enterprise header
          pdf.text(section.content as string, margins.left, y);
          y += lineHeight;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(fontSize);
          
          // Draw table header after enterprise header
          y = drawRow(headers, y, true);
        } else {
          // Draw data row
          y = drawRow(section.content as string[], y);
          
          // Subtle dividing lines between rows (only if not the last row)
          if (i < endSection - 1 && pdfSections[i + 1]?.type === 'data') {
            pdf.setDrawColor(230, 230, 230);
            pdf.setLineWidth(0.1);
            pdf.line(margins.left, y - lineHeight / 2, pageWidth - margins.right, y - lineHeight / 2);
          }
        }
      }
      
      // On the last page, add user and date information at the bottom
      if (pageNum === totalPages - 1) {
        y += lineHeight; // small spacing before user/date
        const {
          user: { profile },
        } = getAppBarState().appState;
        const now = new Date();
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        const userDateText = `${profile.given_name} ${formatToShortLocalDateTime(now, 'DD-MM-YYYY h:mm:ss a')}`;
        pdf.text(userDateText, pageWidth - margins.right, y, { align: 'right' });
      }
    }
    
    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert(`Error generating PDF: ${error.message || 'Unknown error'}`);
  }
}

// Print-specific styles for page size, orientation, and font
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @media print {
      @page {
        size: letter portrait;
        margin: 0mm;
      }
      body, .MuiPaper-root, .MuiTableCell-root, .MuiTypography-root, .MuiTable-root {
        font-family: Roboto, Arial, sans-serif !important;
        font-size: 12px !important;
      }
    }
  `;
  document.head.appendChild(style);
}
